import { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Package,
  User,
  LogOut,
  ShoppingBag,
  Clock,
  CheckCircle,
  Truck,
  XCircle,
  ChevronDown,
  Edit,
  X,
  Calendar,
  Phone as PhoneIcon,
  FileText,
  MapPin,
  BarChart3,
  TrendingUp,
  Heart,
  DollarSign,
  Store,
  CalendarClock,
} from 'lucide-react';
import { clientAuthService, UpdateClientProfileDto } from '@/services/client-auth.service';
import { ordersService } from '@/services/orders.service';
import { internalOrdersService } from '@/services/internal-orders.service';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Separator,
  useToast,
  Input,
} from '@caruso/ui';
import axios from 'axios';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';

export function ClientDashboard() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [activeTab, setActiveTab] = useState<'dados' | 'stats'>('dados');
  const [expandedOrders, setExpandedOrders] = useState<Set<string>>(new Set());
  const [showEditModal, setShowEditModal] = useState(false);
  const [isLoadingCep, setIsLoadingCep] = useState(false);
  const [formData, setFormData] = useState<UpdateClientProfileDto>({});

  useEffect(() => {
    if (!clientAuthService.isAuthenticated()) {
      navigate('/client/login');
    } else {
      setIsAuthenticated(true);
    }
  }, [navigate]);

  const { data: profile, isLoading: loadingProfile } = useQuery({
    queryKey: ['client-profile'],
    queryFn: () => clientAuthService.getProfile(),
    enabled: isAuthenticated,
    retry: false,
  });

  const { data: ordersData, isLoading: loadingOrders } = useQuery({
    queryKey: ['client-orders'],
    queryFn: () => ordersService.getAll(1, 100),
    enabled: isAuthenticated,
  });

  const { data: checkInsData, isLoading: loadingCheckIns } = useQuery({
    queryKey: ['client-check-ins'],
    queryFn: () => internalOrdersService.getRecentCheckIns(100),
    enabled: isAuthenticated && activeTab === 'stats',
  });

  // Calculate statistics
  const statistics = useMemo(() => {
    if (!ordersData?.data || !profile) {
      return {
        totalOrders: 0,
        totalCheckIns: 0,
        averageTicket: 0,
        favoriteProducts: [],
        customerSince: '',
        totalSpent: 0,
      };
    }

    const orders = ordersData.data;
    const totalOrders = orders.length;
    const totalSpent = orders.reduce((sum, order) => sum + order.total, 0);
    const averageTicket = totalOrders > 0 ? totalSpent / totalOrders : 0;

    // Calculate favorite products
    const productMap = new Map<string, { name: string; count: number; total: number }>();
    orders.forEach((order) => {
      order.items?.forEach((item) => {
        const existing = productMap.get(item.productId);
        if (existing) {
          existing.count += item.quantity;
          existing.total += item.subtotal;
        } else {
          productMap.set(item.productId, {
            name: item.productName,
            count: item.quantity,
            total: item.subtotal,
          });
        }
      });
    });

    const favoriteProducts = Array.from(productMap.entries())
      .map(([id, data]) => ({ id, ...data }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 5);

    // Calculate customer since - use first order date if available
    let customerSince = '';
    if (orders.length > 0) {
      const oldestOrder = orders.reduce((oldest, order) => {
        return new Date(order.createdAt) < new Date(oldest.createdAt) ? order : oldest;
      });
      customerSince = formatDistanceToNow(new Date(oldestOrder.createdAt), {
        addSuffix: true,
        locale: ptBR,
      });
    }

    return {
      totalOrders,
      totalCheckIns: checkInsData?.length || 0,
      averageTicket,
      favoriteProducts,
      customerSince,
      totalSpent,
    };
  }, [ordersData, checkInsData, profile]);

  const updateProfileMutation = useMutation({
    mutationFn: clientAuthService.updateProfile,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['client-profile'] });
      setShowEditModal(false);
      toast({
        title: 'Perfil atualizado!',
        description: 'Suas informações foram atualizadas com sucesso',
        variant: 'success',
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Erro ao atualizar perfil',
        description: error.response?.data?.message || 'Tente novamente mais tarde',
        variant: 'destructive',
      });
    },
  });

  const formatPhone = (value: string) => {
    const numbers = value.replace(/\D/g, '');
    if (numbers.length <= 10) {
      return numbers.replace(/(\d{2})(\d)/, '($1) $2').replace(/(\d{4})(\d)/, '$1-$2');
    }
    return numbers
      .replace(/(\d{2})(\d)/, '($1) $2')
      .replace(/(\d{5})(\d)/, '$1-$2')
      .slice(0, 15);
  };

  const formatCPF = (value: string) => {
    const numbers = value.replace(/\D/g, '');
    return numbers
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d{1,2})/, '$1-$2')
      .slice(0, 14);
  };

  const formatCEP = (value: string) => {
    const numbers = value.replace(/\D/g, '');
    return numbers.replace(/(\d{5})(\d)/, '$1-$2').slice(0, 9);
  };

  const handleOpenEditModal = () => {
    if (profile) {
      setFormData({
        name: profile.name || '',
        phone: profile.phone || '',
        document: profile.document || '',
        birthDate: profile.birthDate ? profile.birthDate.split('T')[0] : '',
        zipCode: profile.zipCode || '',
        address: profile.address || '',
        number: profile.number || '',
        complement: profile.complement || '',
        neighborhood: profile.neighborhood || '',
        city: profile.city || '',
        state: profile.state || '',
      });
    }
    setShowEditModal(true);
  };

  const handleInputChange = (field: keyof UpdateClientProfileDto, value: string) => {
    let formattedValue = value;
    if (field === 'phone') {
      formattedValue = formatPhone(value);
    } else if (field === 'document') {
      formattedValue = formatCPF(value);
    } else if (field === 'zipCode') {
      formattedValue = formatCEP(value);
    }
    setFormData((prev) => ({ ...prev, [field]: formattedValue }));
  };

  const handleCepBlur = async () => {
    const cep = formData.zipCode?.replace(/\D/g, '') || '';
    if (cep.length !== 8) return;

    setIsLoadingCep(true);
    try {
      const response = await axios.get(`https://viacep.com.br/ws/${cep}/json/`);
      if (response.data.erro) {
        toast({
          title: 'CEP não encontrado',
          variant: 'destructive',
        });
        return;
      }

      setFormData((prev) => ({
        ...prev,
        address: response.data.logradouro || '',
        neighborhood: response.data.bairro || '',
        city: response.data.localidade || '',
        state: response.data.uf || '',
      }));
    } catch (error) {
      toast({
        title: 'Erro ao buscar CEP',
        variant: 'destructive',
      });
    } finally {
      setIsLoadingCep(false);
    }
  };

  const handleSubmitProfile = (e: React.FormEvent) => {
    e.preventDefault();

    // Remove formatting before sending
    const dataToSend: UpdateClientProfileDto = { ...formData };
    if (dataToSend.phone) dataToSend.phone = dataToSend.phone.replace(/\D/g, '');
    if (dataToSend.document) dataToSend.document = dataToSend.document.replace(/\D/g, '');
    if (dataToSend.zipCode) dataToSend.zipCode = dataToSend.zipCode.replace(/\D/g, '');

    updateProfileMutation.mutate(dataToSend);
  };

  const handleLogout = () => {
    clientAuthService.logout();
    toast({
      title: 'Logout realizado',
      description: 'Até logo!',
    });
    navigate('/client/login');
  };

  const toggleOrderExpanded = (orderId: string) => {
    setExpandedOrders((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(orderId)) {
        newSet.delete(orderId);
      } else {
        newSet.add(orderId);
      }
      return newSet;
    });
  };

  const statusConfig = {
    pending: {
      label: 'Pendente',
      icon: Clock,
      color: 'text-yellow-400',
      bg: 'bg-yellow-500/20',
    },
    confirmed: {
      label: 'Confirmado',
      icon: CheckCircle,
      color: 'text-blue-400',
      bg: 'bg-blue-500/20',
    },
    processing: {
      label: 'Processando',
      icon: Package,
      color: 'text-purple-400',
      bg: 'bg-purple-500/20',
    },
    shipped: {
      label: 'Enviado',
      icon: Truck,
      color: 'text-cyan-400',
      bg: 'bg-cyan-500/20',
    },
    delivered: {
      label: 'Entregue',
      icon: CheckCircle,
      color: 'text-green-400',
      bg: 'bg-green-500/20',
    },
    cancelled: {
      label: 'Cancelado',
      icon: XCircle,
      color: 'text-red-400',
      bg: 'bg-red-500/20',
    },
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(price);
  };

  const formatDate = (date: string) => {
    return new Intl.DateTimeFormat('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(date));
  };

  if (!isAuthenticated) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-neutral-950 via-neutral-900 to-neutral-950">
      {/* Header */}
      <div className="bg-neutral-900/50 backdrop-blur border-b border-neutral-800">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-brand-gold/10 rounded-full flex items-center justify-center">
                <User className="w-5 h-5 text-brand-gold" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-white">
                  {loadingProfile ? 'Carregando...' : profile?.name}
                </h1>
                <p className="text-sm text-neutral-400">{profile?.email}</p>
              </div>
            </div>
            <Button
              onClick={handleLogout}
              variant="outline"
              className="border-neutral-700 text-neutral-300 hover:bg-neutral-800"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Sair
            </Button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 py-8">
        {/* Tabs */}
        <div className="mb-6">
          <div className="flex gap-2 bg-neutral-900/50 backdrop-blur border border-neutral-800 rounded-lg p-1.5 w-fit">
            <button
              onClick={() => setActiveTab('dados')}
              className={`px-6 py-2.5 rounded-lg transition-all font-medium ${
                activeTab === 'dados'
                  ? 'bg-brand-gold text-black shadow-lg'
                  : 'text-neutral-400 hover:text-white hover:bg-neutral-800/50'
              }`}
            >
              <User className="w-4 h-4 inline mr-2" />
              Meus Dados
            </button>
            <button
              onClick={() => setActiveTab('stats')}
              className={`px-6 py-2.5 rounded-lg transition-all font-medium ${
                activeTab === 'stats'
                  ? 'bg-brand-gold text-black shadow-lg'
                  : 'text-neutral-400 hover:text-white hover:bg-neutral-800/50'
              }`}
            >
              <BarChart3 className="w-4 h-4 inline mr-2" />
              Estatísticas
            </button>
          </div>
        </div>

        {activeTab === 'dados' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Profile Card */}
            <Card className="bg-neutral-900/50 backdrop-blur border-neutral-800 lg:col-span-1">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-white flex items-center gap-2">
                    <User className="w-5 h-5 text-brand-gold" />
                    Meu Perfil
                  </CardTitle>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleOpenEditModal}
                    className="text-brand-gold hover:text-brand-gold/80"
                  >
                    <Edit className="w-4 h-4" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                {loadingProfile ? (
                  <div className="text-center py-4 text-neutral-400">Carregando...</div>
                ) : (
                  <>
                    <div>
                      <p className="text-xs text-neutral-500">Nome</p>
                      <p className="text-sm text-neutral-300">{profile?.name}</p>
                    </div>
                    <div>
                      <p className="text-xs text-neutral-500">E-mail</p>
                      <p className="text-sm text-neutral-300">{profile?.email}</p>
                    </div>
                    {profile?.phone && (
                      <div>
                        <p className="text-xs text-neutral-500">Telefone</p>
                        <p className="text-sm text-neutral-300">{profile.phone}</p>
                      </div>
                    )}
                    {profile?.document && (
                      <div>
                        <p className="text-xs text-neutral-500">CPF</p>
                        <p className="text-sm text-neutral-300">{profile.document}</p>
                      </div>
                    )}
                    {profile?.category && (
                      <div>
                        <p className="text-xs text-neutral-500">Categoria</p>
                        <div className="flex items-center gap-2 mt-1">
                          <span className="text-sm text-neutral-300">{profile.category.name}</span>
                          {profile.category.discount && (
                            <span className="text-xs px-2 py-0.5 bg-brand-gold/20 text-brand-gold rounded">
                              {profile.category.discount}% desconto
                            </span>
                          )}
                        </div>
                      </div>
                    )}
                    {profile?.address && (
                      <div>
                        <p className="text-xs text-neutral-500">Endereço</p>
                        <p className="text-sm text-neutral-300">
                          {profile.address}, {profile.number}
                          {profile.complement && ` - ${profile.complement}`}
                        </p>
                        <p className="text-sm text-neutral-300">
                          {profile.neighborhood} - {profile.city}/{profile.state}
                        </p>
                        <p className="text-sm text-neutral-300">CEP: {profile.zipCode}</p>
                      </div>
                    )}
                  </>
                )}
              </CardContent>
            </Card>

            {/* Orders List */}
            <Card className="bg-neutral-900/50 backdrop-blur border-neutral-800 lg:col-span-2">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <ShoppingBag className="w-5 h-5 text-brand-gold" />
                  Meus Pedidos
                </CardTitle>
              </CardHeader>
              <CardContent>
                {loadingOrders ? (
                  <div className="text-center py-8 text-neutral-400">Carregando pedidos...</div>
                ) : !ordersData?.data || ordersData.data.length === 0 ? (
                  <div className="text-center py-8 text-neutral-400">
                    <Package className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>Você ainda não tem pedidos</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {ordersData.data.map((order) => {
                      const status = statusConfig[order.status as keyof typeof statusConfig];
                      const StatusIcon = status?.icon || Package;
                      const isExpanded = expandedOrders.has(order.id);

                      return (
                        <Card key={order.id} className="bg-neutral-800/50 border-neutral-700">
                          <CardContent className="p-4">
                            <div className="flex items-start justify-between mb-3">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-2">
                                  <span className="text-sm font-mono text-neutral-400">
                                    #{order.orderNumber}
                                  </span>
                                  <span
                                    className={`flex items-center gap-1 text-xs px-2 py-1 rounded ${status?.bg} ${status?.color}`}
                                  >
                                    <StatusIcon className="w-3 h-3" />
                                    {status?.label}
                                  </span>
                                </div>
                                <p className="text-xs text-neutral-500">
                                  {formatDate(order.createdAt)}
                                </p>
                              </div>
                              <div className="text-right">
                                <p className="text-lg font-bold text-brand-gold">
                                  {formatPrice(order.total)}
                                </p>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="text-neutral-400 hover:text-white mt-1"
                                  onClick={() => toggleOrderExpanded(order.id)}
                                >
                                  {isExpanded ? 'Ocultar' : 'Ver detalhes'}
                                  <ChevronDown
                                    className={`w-4 h-4 ml-1 transition-transform ${
                                      isExpanded ? 'rotate-180' : ''
                                    }`}
                                  />
                                </Button>
                              </div>
                            </div>

                            {isExpanded && (
                              <>
                                <Separator className="my-3 bg-neutral-700" />
                                <div className="space-y-2">
                                  <p className="text-sm font-medium text-neutral-300">Itens:</p>
                                  {order.items?.map((item, idx) => (
                                    <div
                                      key={idx}
                                      className="flex items-center justify-between text-sm"
                                    >
                                      <span className="text-neutral-400">
                                        {item.quantity}x {item.productName || 'Produto'}
                                      </span>
                                      <span className="text-neutral-300">
                                        {formatPrice(item.price * item.quantity)}
                                      </span>
                                    </div>
                                  ))}
                                </div>
                              </>
                            )}
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {activeTab === 'stats' && (
          <div className="space-y-6">
            {/* Stats Overview */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <Card className="bg-neutral-900/50 backdrop-blur border-neutral-800">
                <CardContent className="p-6">
                  <div className="flex items-center gap-3">
                    <div className="p-3 bg-brand-gold/10 rounded-lg">
                      <ShoppingBag className="w-6 h-6 text-brand-gold" />
                    </div>
                    <div>
                      <p className="text-xs text-neutral-500">Total de Pedidos</p>
                      <p className="text-2xl font-bold text-white">{statistics.totalOrders}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-neutral-900/50 backdrop-blur border-neutral-800">
                <CardContent className="p-6">
                  <div className="flex items-center gap-3">
                    <div className="p-3 bg-green-500/10 rounded-lg">
                      <Store className="w-6 h-6 text-green-400" />
                    </div>
                    <div>
                      <p className="text-xs text-neutral-500">Check-ins</p>
                      <p className="text-2xl font-bold text-white">{statistics.totalCheckIns}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-neutral-900/50 backdrop-blur border-neutral-800">
                <CardContent className="p-6">
                  <div className="flex items-center gap-3">
                    <div className="p-3 bg-blue-500/10 rounded-lg">
                      <DollarSign className="w-6 h-6 text-blue-400" />
                    </div>
                    <div>
                      <p className="text-xs text-neutral-500">Ticket Médio</p>
                      <p className="text-2xl font-bold text-white">
                        {formatPrice(statistics.averageTicket)}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-neutral-900/50 backdrop-blur border-neutral-800">
                <CardContent className="p-6">
                  <div className="flex items-center gap-3">
                    <div className="p-3 bg-purple-500/10 rounded-lg">
                      <CalendarClock className="w-6 h-6 text-purple-400" />
                    </div>
                    <div>
                      <p className="text-xs text-neutral-500">Cliente desde</p>
                      <p className="text-sm font-bold text-white">
                        {statistics.customerSince || 'N/A'}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Favorite Products */}
              <Card className="bg-neutral-900/50 backdrop-blur border-neutral-800">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <Heart className="w-5 h-5 text-brand-gold" />
                    Produtos Favoritos
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {statistics.favoriteProducts.length > 0 ? (
                    <div className="space-y-3">
                      {statistics.favoriteProducts.map((product, index) => (
                        <div
                          key={product.id}
                          className="flex items-center justify-between p-3 bg-neutral-800/50 rounded-lg border border-neutral-700"
                        >
                          <div className="flex items-center gap-3">
                            <div className="flex items-center justify-center w-8 h-8 bg-brand-gold/10 rounded-full">
                              <span className="text-sm font-bold text-brand-gold">{index + 1}</span>
                            </div>
                            <div>
                              <p className="text-sm font-medium text-white">{product.name}</p>
                              <p className="text-xs text-neutral-500">
                                {product.count} unidades compradas
                              </p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="text-sm font-bold text-brand-gold">
                              {formatPrice(product.total)}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-neutral-400">
                      <Heart className="w-12 h-12 mx-auto mb-3 opacity-50" />
                      <p>Nenhuma compra realizada ainda</p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Recent Check-ins */}
              <Card className="bg-neutral-900/50 backdrop-blur border-neutral-800">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <Store className="w-5 h-5 text-brand-gold" />
                    Check-ins Recentes
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {loadingCheckIns ? (
                    <div className="text-center py-8 text-neutral-400">Carregando check-ins...</div>
                  ) : checkInsData && checkInsData.length > 0 ? (
                    <div className="space-y-2 max-h-[400px] overflow-y-auto">
                      {checkInsData.slice(0, 10).map((checkIn: any, index: number) => (
                        <div
                          key={checkIn.id || index}
                          className="flex items-center justify-between p-3 bg-neutral-800/50 rounded-lg border border-neutral-700"
                        >
                          <div className="flex items-center gap-3">
                            <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                            <div>
                              <p className="text-sm text-white">
                                Check-in #{checkIn.orderNumber || index + 1}
                              </p>
                              <p className="text-xs text-neutral-500">
                                {checkIn.createdAt
                                  ? formatDate(checkIn.createdAt)
                                  : 'Data não disponível'}
                              </p>
                            </div>
                          </div>
                          {checkIn.total && (
                            <p className="text-sm font-medium text-brand-gold">
                              {formatPrice(checkIn.total)}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-neutral-400">
                      <Store className="w-12 h-12 mx-auto mb-3 opacity-50" />
                      <p>Nenhum check-in realizado ainda</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Total Spent Card */}
            <Card className="bg-gradient-to-br from-brand-gold/10 to-transparent backdrop-blur border-brand-gold/20">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="p-4 bg-brand-gold/20 rounded-lg">
                      <TrendingUp className="w-8 h-8 text-brand-gold" />
                    </div>
                    <div>
                      <p className="text-sm text-neutral-400">Total Gasto</p>
                      <p className="text-4xl font-bold text-white">
                        {formatPrice(statistics.totalSpent)}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-neutral-400">em {statistics.totalOrders} pedidos</p>
                    <p className="text-lg font-semibold text-brand-gold">
                      Média: {formatPrice(statistics.averageTicket)}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>

      {/* Edit Profile Modal */}
      <AnimatePresence>
        {showEditModal && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40"
              onClick={() => setShowEditModal(false)}
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="fixed inset-0 flex items-center justify-center z-50 p-4 pointer-events-none"
            >
              <Card className="w-full max-w-2xl bg-neutral-900 border-neutral-800 max-h-[90vh] overflow-y-auto pointer-events-auto">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-white">Editar Perfil</CardTitle>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setShowEditModal(false)}
                      className="text-neutral-400 hover:text-white"
                    >
                      <X className="w-5 h-5" />
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleSubmitProfile} className="space-y-4">
                    {/* Personal Information */}
                    <div className="space-y-4">
                      <h3 className="text-lg font-medium text-white">Informações Pessoais</h3>

                      <div>
                        <label className="block text-sm text-neutral-400 mb-2">Nome *</label>
                        <Input
                          value={formData.name || ''}
                          onChange={(e) => handleInputChange('name', e.target.value)}
                          className="bg-neutral-800/50 border-neutral-700 text-white"
                          required
                        />
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm text-neutral-400 mb-2">Telefone</label>
                          <Input
                            value={formData.phone || ''}
                            onChange={(e) => handleInputChange('phone', e.target.value)}
                            placeholder="(00) 00000-0000"
                            className="bg-neutral-800/50 border-neutral-700 text-white"
                          />
                        </div>

                        <div>
                          <label className="block text-sm text-neutral-400 mb-2">CPF</label>
                          <Input
                            value={formData.document || ''}
                            onChange={(e) => handleInputChange('document', e.target.value)}
                            placeholder="000.000.000-00"
                            className="bg-neutral-800/50 border-neutral-700 text-white"
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm text-neutral-400 mb-2">
                          Data de Nascimento
                        </label>
                        <Input
                          type="date"
                          value={formData.birthDate || ''}
                          onChange={(e) => handleInputChange('birthDate', e.target.value)}
                          className="bg-neutral-800/50 border-neutral-700 text-white"
                        />
                      </div>
                    </div>

                    <Separator className="bg-neutral-800" />

                    {/* Address */}
                    <div className="space-y-4">
                      <h3 className="text-lg font-medium text-white">Endereço</h3>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm text-neutral-400 mb-2">CEP</label>
                          <Input
                            value={formData.zipCode || ''}
                            onChange={(e) => handleInputChange('zipCode', e.target.value)}
                            onBlur={handleCepBlur}
                            placeholder="00000-000"
                            className="bg-neutral-800/50 border-neutral-700 text-white"
                            disabled={isLoadingCep}
                          />
                          {isLoadingCep && (
                            <p className="text-xs text-neutral-500 mt-1">Buscando CEP...</p>
                          )}
                        </div>

                        <div>
                          <label className="block text-sm text-neutral-400 mb-2">Estado</label>
                          <Input
                            value={formData.state || ''}
                            onChange={(e) => handleInputChange('state', e.target.value)}
                            placeholder="UF"
                            maxLength={2}
                            className="bg-neutral-800/50 border-neutral-700 text-white"
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm text-neutral-400 mb-2">Cidade</label>
                          <Input
                            value={formData.city || ''}
                            onChange={(e) => handleInputChange('city', e.target.value)}
                            className="bg-neutral-800/50 border-neutral-700 text-white"
                          />
                        </div>

                        <div>
                          <label className="block text-sm text-neutral-400 mb-2">Bairro</label>
                          <Input
                            value={formData.neighborhood || ''}
                            onChange={(e) => handleInputChange('neighborhood', e.target.value)}
                            className="bg-neutral-800/50 border-neutral-700 text-white"
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="md:col-span-2">
                          <label className="block text-sm text-neutral-400 mb-2">
                            Rua/Logradouro
                          </label>
                          <Input
                            value={formData.address || ''}
                            onChange={(e) => handleInputChange('address', e.target.value)}
                            className="bg-neutral-800/50 border-neutral-700 text-white"
                          />
                        </div>

                        <div>
                          <label className="block text-sm text-neutral-400 mb-2">Número</label>
                          <Input
                            value={formData.number || ''}
                            onChange={(e) => handleInputChange('number', e.target.value)}
                            className="bg-neutral-800/50 border-neutral-700 text-white"
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm text-neutral-400 mb-2">Complemento</label>
                        <Input
                          value={formData.complement || ''}
                          onChange={(e) => handleInputChange('complement', e.target.value)}
                          placeholder="Apto, Bloco, etc"
                          className="bg-neutral-800/50 border-neutral-700 text-white"
                        />
                      </div>
                    </div>

                    <div className="flex items-center gap-3 pt-4">
                      <Button
                        type="button"
                        variant="outline"
                        className="flex-1 border-neutral-700 text-neutral-300"
                        onClick={() => setShowEditModal(false)}
                      >
                        Cancelar
                      </Button>
                      <Button
                        type="submit"
                        className="flex-1 bg-brand-gold hover:bg-brand-gold/90 text-black"
                        disabled={updateProfileMutation.isPending}
                      >
                        {updateProfileMutation.isPending ? 'Salvando...' : 'Salvar Alterações'}
                      </Button>
                    </div>
                  </form>
                </CardContent>
              </Card>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
}
